import { Receipt } from "@phosphor-icons/react";
import { ReactNode } from "react";
import { formatAsMoney } from "../../../../helpers/numbers";

export type BillSummaryRow = {
  label: string;
  value: string;
};

interface BillSummaryCardProps {
  rows: BillSummaryRow[];
  amount: number;
  canProceed: boolean;
  onProceed: () => void;
  /** Alerts (insufficient balance, purchase errors, etc.) shown above the CTA. */
  alerts?: ReactNode;
  ctaLabel?: string;
  footerNote?: string;
}

/**
 * Compact summary + CTA. On mobile it sits directly under the short form (no
 * scrolling past long lists); on desktop it's sticky in the right column.
 */
const BillSummaryCard = ({
  rows,
  amount,
  canProceed,
  onProceed,
  alerts,
  ctaLabel = "Continue",
  footerNote = "Payment is made from your wallet after PIN confirmation.",
}: BillSummaryCardProps) => {
  return (
    <aside className="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-deepBlue/10 text-deepBlue flex items-center justify-center">
          <Receipt size={22} weight="bold" />
        </div>
        <div>
          <h2 className="font-bold text-lg text-gray-900">Summary</h2>
          <p className="text-sm text-gray-500">Review before payment.</p>
        </div>
      </div>

      <div className="divide-y divide-gray-100 border-y border-gray-100">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-4 py-3 text-sm"
          >
            <span className="text-gray-500 shrink-0">{row.label}</span>
            <span className="font-medium text-gray-800 text-right truncate">
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">Total</span>
        <span className="text-xl font-bold text-gray-900">
          NGN {formatAsMoney(amount || 0)}
        </span>
      </div>

      {alerts}

      <button
        type="button"
        disabled={!canProceed}
        onClick={onProceed}
        className="p-3 px-5 rounded-xl bg-deepBlue text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {ctaLabel}
      </button>
      <p className="text-xs text-gray-500 text-center">{footerNote}</p>
    </aside>
  );
};

export default BillSummaryCard;
